class WishlistManager {
  constructor() {
    this.STORAGE_KEY = 'treasures-wishlist';
    this.wishlist = this.loadWishlist();
    this.init();
  }

  init() {
    this.attachEventListeners();
    // Initial update - with retry for dynamically loaded content
    this.updateAllWishlistButtons();
    // Retry after a short delay in case DOM is still loading
    setTimeout(() => this.updateAllWishlistButtons(), 500);
    setTimeout(() => this.updateAllWishlistButtons(), 1500);

    // Watch for dynamically added buttons
    this.observeDOM();
  }

  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to load wishlist:', e);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.wishlist));
    } catch (e) {
      console.error('Failed to save wishlist:', e);
    }
  }

  attachEventListeners() {
    document.addEventListener('click', (e) => {
      const wishlistBtn = e.target.closest('[data-wishlist-btn]');
      if (wishlistBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.handleWishlistClick(wishlistBtn);
      }
    });
  }

  observeDOM() {
    // Watch for new product cards being added to the DOM
    const observer = new MutationObserver(() => {
      this.updateAllWishlistButtons();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false
    });
  }

  handleWishlistClick(button) {
    const productHandle = button.dataset.productHandle;
    const productId = button.dataset.productId;
    const productTitle = button.dataset.productTitle;
    const productImage = button.dataset.productImage;

    console.log('Wishlist click - Handle:', productHandle, 'Image:', productImage);

    if (this.isInWishlist(productHandle)) {
      this.removeFromWishlist(productHandle);
      this.updateButton(button, false);
    } else {
      // Validate image URL
      if (!productImage || productImage.trim() === '') {
        console.warn('No image URL for product:', productHandle);
      }

      this.addToWishlist({
        handle: productHandle,
        id: productId,
        title: productTitle,
        image: productImage,
        addedAt: new Date().toISOString()
      });
      this.updateButton(button, true);
      this.showNotification(productTitle, productImage);
    }
  }

  addToWishlist(product) {
    if (!this.isInWishlist(product.handle)) {
      this.wishlist.push(product);
      this.saveWishlist();
      this.dispatchUpdateEvent();
    }
  }

  removeFromWishlist(productHandle) {
    this.wishlist = this.wishlist.filter(item => item.handle !== productHandle);
    this.saveWishlist();
    this.dispatchUpdateEvent();
  }

  isInWishlist(productHandle) {
    return this.wishlist.some(item => item.handle === productHandle);
  }

  updateButton(button, isActive) {
    if (isActive) {
      button.classList.add('wishlist-active');
      button.setAttribute('aria-pressed', 'true');
      button.setAttribute('aria-label', 'Remove from wishlist');
    } else {
      button.classList.remove('wishlist-active');
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', 'Add to wishlist');
    }
  }

  updateAllWishlistButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-btn]');

    if (buttons.length === 0) {
      console.warn('No wishlist buttons found in DOM');
      return;
    }

    buttons.forEach(button => {
      const productHandle = button.dataset.productHandle;
      if (!productHandle) {
        console.warn('Button missing product handle:', button);
        return;
      }
      const isActive = this.isInWishlist(productHandle);
      this.updateButton(button, isActive);
    });
  }

  showNotification(productTitle, productImage) {
    // Validate image URL
    if (!productImage || productImage.trim() === '') {
      console.error('Cannot show notification - no image URL provided');
      return;
    }

    // Remove existing notification if any
    const existingNotification = document.querySelector('.tws-wishlist-pop-up');
    if (existingNotification) {
      existingNotification.remove();
    }

    // Create notification element
    const notification = document.createElement('aside');
    notification.className = 'tws-wishlist-pop-up';
    notification.setAttribute('role', 'dialog');
    notification.setAttribute('aria-labelledby', 'wishlist-message');
    notification.setAttribute('aria-live', 'polite');

    notification.innerHTML = `
      <img class="tws-group" src="${productImage}" alt="${productTitle}" onerror="console.error('Failed to load image:', this.src)" />
      <p class="tws-text-wrapper" id="wishlist-message">${productTitle} has been added to your wishlist.</p>
      <button class="tws-button" type="button" aria-label="View your wishlist">
        <span class="tws-button-text">View Wishlist</span>
      </button>
      <button class="tws-close" type="button" aria-label="Close notification">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    `;

    document.body.appendChild(notification);

    // Close button
    notification.querySelector('.tws-close').addEventListener('click', () => {
      notification.remove();
    });

    // View Wishlist button
    notification.querySelector('.tws-button').addEventListener('click', () => {
      window.location.href = '/pages/wishlist';
    });

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 5000);
  }

  getWishlist() {
    return this.wishlist;
  }

  clearWishlist() {
    this.wishlist = [];
    this.saveWishlist();
    this.updateAllWishlistButtons();
    this.dispatchUpdateEvent();
  }

  dispatchUpdateEvent() {
    // Dispatch custom event for wishlist updates
    const event = new CustomEvent('wishlist:updated', {
      detail: { wishlist: this.wishlist }
    });
    document.dispatchEvent(event);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new WishlistManager();
  });
} else {
  window.wishlistManager = new WishlistManager();
}
